/**
 * UI-8b Task 4 — PlanDrawer.
 *
 * Create / edit / view drawer for a Plan row. Three fields:
 *
 *   1. name                — FormText, required, 1..100 chars
 *   2. maxAgents           — FormNumber, 1..10000
 *   3. maxConcurrentCalls  — FormNumber, 1..50000
 *   4. features            — Checkbox list of available plan features
 *
 * Mirrors the UI-9a forms standard (ZodForm + Form* wrappers) and the
 * default `width="standard"` ResourceDrawer chrome. The drawer is purely
 * controlled — the parent (PlansPage) owns `open` + `existing` and calls
 * `onSaved()` to refetch and close after a successful mutation.
 */
import { useRef, useState, useEffect, type JSX } from 'react'

import ResourceDrawer from '../ResourceDrawer.js'
import { ZodForm, FormText, FormNumber } from '../../forms/index.js'
import { usePlans, usePlanFeatures, type Plan, type PlanFeature } from '../../../hooks/usePlans.js'
import { useToast } from '../../system/ToastProvider.js'
import { PlanSchema, type PlanFormValues } from './validation.js'
import { MultiSelect } from 'primereact/multiselect'

export type PlanDrawerMode = 'create' | 'edit' | 'view'

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface PlanDrawerProps {
  open: boolean
  /** The row being edited/viewed; `null` = create mode. */
  existing: Plan | null
  onClose: () => void
  /** Called after a successful save so the page can refetch + close. */
  onSaved: () => void
  /** Features list to use (passed from parent for real-time updates) */
  features?: PlanFeature[]
  /** Mode: create, edit, or view (fields disabled in view mode) */
  mode?: PlanDrawerMode
}

export function PlanDrawer({
  open,
  existing,
  onClose,
  onSaved,
  features: propFeatures,
  mode = existing ? 'edit' : 'create',
}: PlanDrawerProps): JSX.Element {
  const toast = useToast()
  const { create, update } = usePlans()
  const { items: hookFeatures } = usePlanFeatures()
  const features = (propFeatures ?? hookFeatures).filter(f => f.enabled)
  const formRef = useRef<HTMLFormElement>(null)
  const [saving, setSaving] = useState(false)
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([])

  const isView = mode === 'view'
  const isEdit = mode === 'edit'
  /** Remount key — flips when the drawer opens for a different row. */
  const contentKey = `${mode}-${existing?.id ?? 'new'}`

  const defaults: PlanFormValues = existing
    ? {
        name: existing.name,
        maxAgents: existing.maxAgents,
        maxConcurrentCalls: existing.maxConcurrentCalls,
        features: (existing.features as Array<{ id: string; name: string }>).map((f) => f.id),
      }
    : {
        name: '',
        maxAgents: 10,
        maxConcurrentCalls: 20,
        features: [],
      }

  // Reset selected features when drawer opens
  useEffect(() => {
    if (open) {
      const existingFeatures = existing
        ? (existing.features as Array<{ id: string; name: string }>).map((f) => f.id)
        : []
      setSelectedFeatures(existingFeatures)
    } else {
      setSelectedFeatures([])
    }
  }, [open, existing?.id])

  async function handleSubmit(values: PlanFormValues): Promise<void> {
    setSaving(true)
    try {
      // Convert feature IDs array to JSON object
      const featuresObj: Record<string, unknown> = {}
      selectedFeatures.forEach((featureId) => {
        featuresObj[featureId] = true
      })

      const submitData = {
        ...values,
        features: featuresObj,
      }

      if (isEdit && existing) {
        await update(existing.id, submitData)
      } else {
        await create(submitData)
      }
      onSaved()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save plan.')
    } finally {
      setSaving(false)
    }
  }

  async function handleSave(): Promise<void> {
    const form = formRef.current
    if (form) {
      // Get form data from ZodForm
      const formData = new FormData(form)
      const values: PlanFormValues = {
        name: formData.get('name') as string,
        maxAgents: Number(formData.get('maxAgents')),
        maxConcurrentCalls: Number(formData.get('maxConcurrentCalls')),
        features: selectedFeatures,
      }
      await handleSubmit(values)
    }
  }

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      {...(!isView && { onSave: handleSave })}
      formRef={formRef}
      saving={saving}
      width="wide"
      title={
        isView
          ? `View Plan: ${existing?.name ?? ''}`
          : isEdit
            ? `Edit Plan: ${existing?.name ?? ''}`
            : 'New Plan'
      }
    >
      <div className="space-y-5">
        {/* Plan Identity Section */}
        <div className="bg-white rounded-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-50">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Plan Identity</h3>
          </div>
          <div className="p-5">
            <ZodForm
              key={contentKey}
              schema={PlanSchema}
              defaultValues={defaults}
              onSubmit={(data) => handleSubmit(data)}
              formRef={formRef}
            >
              <div className="space-y-4">
                <FormText
                  name="name"
                  label="Name"
                  required
                  disabled={isView}
                  helpText="Human-readable plan name, e.g. 'Pro' or 'Enterprise'."
                />
                <FormNumber
                  name="maxAgents"
                  label="Max Agents"
                  required
                  disabled={isView}
                  min={1}
                  max={10000}
                  helpText="Maximum number of seats permitted under this plan."
                />
                <FormNumber
                  name="maxConcurrentCalls"
                  label="Max Concurrent Calls"
                  required
                  disabled={isView}
                  min={1}
                  max={50000}
                  helpText="Concurrent call cap enforced at the platform tier."
                />
              </div>
              {/* Hidden input to store selected features */}
              <input
                type="hidden"
                name="features"
                value={JSON.stringify(selectedFeatures)}
              />
            </ZodForm>
          </div>
        </div>

        {/* Features Section */}
        <div className="bg-white rounded-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-50">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Features</h3>
          </div>
          <div className="p-5">
            {features.length === 0 ? (
              <p className="text-gray-400 text-sm">No features available. Create features first.</p>
            ) : (
              <MultiSelect
                value={selectedFeatures}
                onChange={(e) => setSelectedFeatures(e.value as string[])}
                options={features.map(f => ({ value: f.id, label: f.name }))}
                optionLabel="label"
                optionValue="value"
                placeholder="Select features"
                display="chip"
                maxSelectedLabels={3}
                selectedItemsLabel="{0} features selected"
                disabled={isView}
                className="w-full"
                filter
                filterPlaceholder="Search features..."
              />
            )}
          </div>
        </div>
      </div>
    </ResourceDrawer>
  )
}

export default PlanDrawer
